/**
 * A szerkesztett pályák tárolója.
 *
 * A forrás (blokkok + téma) a localStorage-ban él, a játék számára pedig
 * TrackDef-et adunk belőle. A fordítás gyorsítótárazott: egy pályát csak
 * akkor fordítunk újra, ha a szerkesztőben megváltozott (updatedAt).
 */
import type { TrackDef, TrackTheme } from '../core/types';
import { compileDoc } from './compile';
import { PIECES, type EditorDoc, type PlacedPiece, type PieceType, newDocId, newUid } from './model';

const KEY = 'skart.tracks.v1';
export const MAX_TRACKS = 24;
const MAX_NAME = 42;

interface StoreShape {
  version: 1;
  docs: EditorDoc[];
}

const memory: StoreShape = { version: 1, docs: [] };
let loaded: StoreShape | null = null;

const available = (): boolean => {
  try {
    return typeof window !== 'undefined' && !!window.localStorage;
  } catch {
    return false;
  }
};

const isTheme = (v: unknown): v is TrackTheme =>
  v === 'city' || v === 'suburb' || v === 'lake' || v === 'circuit' || v === 'desert' || v === 'snow';

const sanitizePiece = (raw: unknown): PlacedPiece | null => {
  if (!raw || typeof raw !== 'object') return null;
  const p = raw as Partial<PlacedPiece>;
  if (typeof p.type !== 'string' || !(p.type in PIECES)) return null;
  if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) return null;
  const rot = Math.round(Number(p.rot) || 0) & 3;
  return {
    uid: typeof p.uid === 'string' && p.uid ? p.uid : newUid(),
    type: p.type as PieceType,
    x: Math.round(Number(p.x)),
    y: Math.round(Number(p.y)),
    rot,
  };
};

const sanitizeDoc = (raw: unknown): EditorDoc | null => {
  if (!raw || typeof raw !== 'object') return null;
  const d = raw as Partial<EditorDoc>;
  if (typeof d.id !== 'string' || !d.id) return null;
  if (!isTheme(d.theme)) return null;
  const pieces = Array.isArray(d.pieces) ? d.pieces.map(sanitizePiece).filter((p): p is PlacedPiece => p !== null) : [];
  return {
    version: 1,
    id: d.id,
    name: (typeof d.name === 'string' ? d.name : 'Saját pálya').slice(0, MAX_NAME),
    theme: d.theme,
    pieces,
    updatedAt: Number.isFinite(d.updatedAt) ? Number(d.updatedAt) : Date.now(),
  };
};

const read = (): StoreShape => {
  if (loaded) return loaded;
  if (!available()) {
    loaded = memory;
    return loaded;
  }
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) {
      loaded = { version: 1, docs: [] };
      return loaded;
    }
    const parsed = JSON.parse(raw) as Partial<StoreShape>;
    const docs = Array.isArray(parsed.docs)
      ? parsed.docs
          .map(sanitizeDoc)
          .filter((d): d is EditorDoc => d !== null)
          .slice(0, MAX_TRACKS)
      : [];
    loaded = { version: 1, docs };
  } catch {
    loaded = { version: 1, docs: [] };
  }
  return loaded;
};

const write = (store: StoreShape): void => {
  loaded = store;
  if (!available()) {
    memory.docs = store.docs;
    return;
  }
  try {
    window.localStorage.setItem(KEY, JSON.stringify(store));
  } catch {
    memory.docs = store.docs;
  }
};

const revCache = new Map<string, { rev: number; def: TrackDef | null }>();

export const listDocs = (): EditorDoc[] => read().docs.map((d) => ({ ...d, pieces: d.pieces.map((p) => ({ ...p })) }));

export const docById = (id: string): EditorDoc | null => read().docs.find((d) => d.id === id) ?? null;

export const saveDoc = (doc: EditorDoc): EditorDoc => {
  const store = read();
  const clean: EditorDoc = {
    version: 1,
    id: doc.id,
    name: (doc.name || 'Saját pálya').slice(0, MAX_NAME),
    theme: doc.theme,
    pieces: doc.pieces.map((p) => ({ ...p })),
    updatedAt: Date.now(),
  };
  const index = store.docs.findIndex((d) => d.id === clean.id);
  if (index >= 0) store.docs[index] = clean;
  else store.docs.unshift(clean);
  if (store.docs.length > MAX_TRACKS) store.docs.length = MAX_TRACKS;
  write({ version: 1, docs: store.docs });
  revCache.delete(clean.id);
  return clean;
};

export const deleteDoc = (id: string): void => {
  const store = read();
  const next = store.docs.filter((d) => d.id !== id);
  if (next.length === store.docs.length) return;
  write({ version: 1, docs: next });
  revCache.delete(id);
};

/** A szerkesztett pályák TrackDef-ként, a játék számára. */
export const customTrackDefs = (): TrackDef[] => {
  const out: TrackDef[] = [];
  for (const doc of read().docs) {
    const def = customTrackDef(doc.id);
    if (def) out.push(def);
  }
  return out;
};

export const customTrackDef = (id: string): TrackDef | null => {
  const doc = docById(id);
  if (!doc) return null;
  const cached = revCache.get(id);
  if (cached && cached.rev === doc.updatedAt) return cached.def;
  const result = compileDoc(doc);
  const def = result.ok ? result.def : null;
  revCache.set(id, { rev: doc.updatedAt, def });
  return def;
};

/** Van-e mentett, szerkesztett pálya? */
export const hasCustomTracks = (): boolean => read().docs.length > 0;

/** Megtelt-e a 24 pályás keret? */
export const isAtCapacity = (): boolean => read().docs.length >= MAX_TRACKS;

export interface TrackFilePayload {
  format: 'soma-kart-track';
  version: 1;
  name: string;
  theme: TrackTheme;
  pieces: { type: PieceType; x: number; y: number; rot: number }[];
}

/** A pálya megosztható, fájlba írható alakja (a belső azonosítók nélkül). */
export const toFilePayload = (doc: EditorDoc): TrackFilePayload => ({
  format: 'soma-kart-track',
  version: 1,
  name: doc.name,
  theme: doc.theme,
  pieces: doc.pieces.map((p) => ({ type: p.type, x: p.x, y: p.y, rot: p.rot })),
});

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;

/** Egy fájlból (JSON) beolvasott pálya értelmezése; az azonosító mindig új. */
export const parseImportedDoc = (raw: unknown): { doc: EditorDoc | null; error: string | null } => {
  if (!isRecord(raw)) return { doc: null, error: 'A fájl nem pálya-adatot tartalmaz.' };
  if (raw.format !== undefined && raw.format !== 'soma-kart-track') {
    return { doc: null, error: 'Ismeretlen fájlformátum – ez nem Soma Kart pálya.' };
  }
  if (!isTheme(raw.theme)) return { doc: null, error: 'Hiányzó vagy ismeretlen téma.' };
  const pieces = Array.isArray(raw.pieces)
    ? raw.pieces
        .map(sanitizePiece)
        .filter((p): p is PlacedPiece => p !== null)
        .map((p) => ({ ...p, uid: newUid() }))
    : [];
  if (pieces.length === 0) return { doc: null, error: 'A fájlban nincs egyetlen pályaelem sem.' };
  const name = (typeof raw.name === 'string' ? raw.name.trim() : '') || 'Importált pálya';
  return {
    doc: {
      version: 1,
      id: newDocId(),
      name: name.slice(0, MAX_NAME),
      theme: raw.theme,
      pieces,
      updatedAt: Date.now(),
    },
    error: null,
  };
};

/** Egyedi név: ha a kért név foglalt, „(másolat)”, „(másolat 2)” stb. */
export const uniqueName = (base: string, suffix = ''): string => {
  const names = new Set(read().docs.map((d) => d.name));
  const make = (n: number): string => {
    if (!suffix) return n === 1 ? base : `${base} ${n}`;
    return n === 1 ? `${base} (${suffix})` : `${base} (${suffix} ${n})`;
  };
  let n = 1;
  while (names.has(make(n))) n += 1;
  return make(n).slice(0, MAX_NAME);
};

/** Fájlból importált pálya mentése az új azonosítóval (a név ütközését kezelve). */
export const importDoc = (raw: unknown): { doc: EditorDoc | null; error: string | null } => {
  const parsed = parseImportedDoc(raw);
  if (!parsed.doc) return parsed;
  const taken = read().docs.some((d) => d.name === parsed.doc?.name);
  const name = taken ? uniqueName(parsed.doc.name, 'másolat') : parsed.doc.name;
  return { doc: saveDoc({ ...parsed.doc, name }), error: null };
};
